import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { SessionLog } from "../../lib/types";

interface WeeklyProgressProps {
  logs: SessionLog[];
}

const WEEKDAY_NAMES = ["一", "二", "三", "四", "五", "六", "日"];

export function WeeklyProgressWidget({ logs }: WeeklyProgressProps) {
  const { weekDays, todayIndex } = useMemo(() => {
    const now = new Date();
    // Monday as index 0, Sunday as index 6
    const dayOfWeek = (now.getDay() + 6) % 7; 
    
    // Generate dates for current week Monday through Sunday
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      
      // Check if there are any done logs for this date
      const hasDone = logs.some((l) => l.date.slice(0, 10) === iso && l.done > 0);
      days.push({
        iso,
        hasDone,
        isToday: i === dayOfWeek,
        isPast: i < dayOfWeek,
        label: WEEKDAY_NAMES[i],
      });
    }

    return { weekDays: days, todayIndex: dayOfWeek };
  }, [logs]);

  return (
    <div className="rounded-2xl bg-white border border-slate-100/90 p-4 sm:p-5 shadow-xs w-full min-w-0 overflow-hidden">
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 min-w-0">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-[13.5px] sm:text-[14px] font-bold text-slate-900 truncate">本週進度</span>
          <span className="text-[10.5px] sm:text-[11.5px] font-grotesk text-slate-400 font-normal truncate hidden xs:inline">Weekly Progress</span>
        </div>
        <Link
          to="/stats"
          className="text-[11.5px] sm:text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-0.5 no-underline transition-colors shrink-0"
        >
          <span>查看詳情</span>
          <span className="font-sans text-[11px]">→</span>
        </Link>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center w-full min-w-0">
        {weekDays.map((day, idx) => {
          const isCurrent = idx === todayIndex;
          const isDone = day.hasDone;

          return (
            <div key={day.iso} className="flex flex-col items-center min-w-0">
              <div
                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all ${
                  isCurrent
                    ? "bg-[#2563EB] shadow-xs text-white"
                    : isDone
                    ? "bg-blue-500 text-white"
                    : "bg-[#E2E8F0]/70"
                }`}
                title={`${day.iso} ${isCurrent ? "(今天)" : isDone ? "(已打卡)" : ""}`}
              >
                {isDone && !isCurrent && (
                  <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className={`text-[10.5px] sm:text-[12px] mt-1.5 sm:mt-2 font-medium ${isCurrent ? "text-blue-600 font-bold" : "text-slate-400"}`}>
                {day.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
